/**
 * effects.ts — Registro de efeitos visuais do Studio (Fase 4)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno). É a única fonte de verdade dos
 * efeitos: os padrões e os blocos passam a REFERENCIAR um efeito pela chave, em
 * vez de carregar CSS solto. Assim o mesmo vidro fosco, brilho ou borda em
 * degradê tem o mesmo desenho no editor, na página publicada, no PDF e no
 * e-mail — e, quando o meio não suporta, cai automaticamente no substituto.
 *
 * Cada efeito declara (contrato obrigatório da Matriz de Capacidades):
 *  - `version`            — versão do desenho, para evoluir sem quebrar o antigo;
 *  - `supportedRenderModes` — onde ele realmente funciona;
 *  - `fallback`           — efeito substituto quando o meio não suporta;
 *  - `performanceCost`    — custo aproximado de renderização;
 *  - `css`                — o desenho, emitido pela própria fonte de estilo.
 *
 * RELEASE: v1.2.0 - 2026-09-11 - Direção de arte por tela, redução automática no
 *            celular e orçamento de desempenho (Fase 7)
 * RELEASE: v1.1.0 - 2026-09-11 - Passa a abrigar também o registro visual da
 *            Fase 6: recortes (máscaras), inclinação e formas decorativas.
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (motor de composições, Fase 4)
 */

/** Modos de saída atendidos pelo Studio. */
export type StudioEffectRenderMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Custo aproximado de renderização do efeito. */
export type StudioEffectCost = 'low' | 'medium' | 'high';

/** Contrato de um efeito visual. */
export interface StudioEffectDefinition {
  key: string;
  label: string;
  /** Frase curta para o painel de propriedades. */
  description?: string;
  version: string;
  supportedRenderModes: StudioEffectRenderMode[];
  /** Efeito usado quando o meio não suporta este. `null` = sem efeito. */
  fallback: string | null;
  performanceCost: StudioEffectCost;
  /** CSS do efeito (classe `.st-fx-<chave>`). */
  css: string;
}

const WEB: StudioEffectRenderMode[] = ['editor', 'published', 'thumbnail'];
const WEB_E_PDF: StudioEffectRenderMode[] = [...WEB, 'pdf'];
const TODOS: StudioEffectRenderMode[] = [...WEB_E_PDF, 'email'];

/**
 * Catálogo de efeitos.
 * Para acrescentar um efeito novo basta registrar aqui: painel, CSS do editor e
 * compilador HTML passam a conhecê-lo sem nenhuma outra alteração.
 */
const EFEITOS: StudioEffectDefinition[] = [
  {
    key: 'none',
    label: 'Nenhum',
    version: '1.0.0',
    supportedRenderModes: TODOS,
    fallback: null,
    performanceCost: 'low',
    css: '',
  },
  {
    key: 'soft',
    label: 'Fundo suave',
    description: 'Camada translúcida discreta, funciona em qualquer meio.',
    version: '1.0.0',
    supportedRenderModes: TODOS,
    fallback: null,
    performanceCost: 'low',
    css: 'background-color: rgba(255,255,255,.10); border: 1px solid rgba(255,255,255,.16);',
  },
  {
    key: 'ring',
    label: 'Contorno fino',
    description: 'Linha sutil em volta do elemento.',
    version: '1.0.0',
    supportedRenderModes: TODOS,
    fallback: null,
    performanceCost: 'low',
    css: 'box-shadow: 0 0 0 1px rgba(148,163,184,.35);',
  },
  {
    key: 'glass',
    label: 'Vidro fosco',
    description: 'Desfoque do que está atrás. No PDF e no e-mail vira fundo suave.',
    version: '1.1.0',
    supportedRenderModes: WEB,
    fallback: 'soft',
    performanceCost: 'medium',
    css:
      'background-color: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.20);' +
      ' backdrop-filter: blur(14px) saturate(140%); -webkit-backdrop-filter: blur(14px) saturate(140%);',
  },
  {
    key: 'glass-dark',
    label: 'Vidro fosco escuro',
    version: '1.1.0',
    supportedRenderModes: WEB,
    fallback: 'soft',
    performanceCost: 'medium',
    css:
      'background-color: rgba(9,12,20,.45); border: 1px solid rgba(255,255,255,.12); color: #f8fafc;' +
      ' backdrop-filter: blur(16px) saturate(130%); -webkit-backdrop-filter: blur(16px) saturate(130%);',
  },
  {
    key: 'elevate',
    label: 'Elevação',
    description: 'Sombra ampla, dá sensação de profundidade.',
    version: '1.0.0',
    supportedRenderModes: WEB_E_PDF,
    fallback: 'ring',
    performanceCost: 'low',
    css: 'box-shadow: 0 34px 70px -24px rgba(15,23,42,.45);',
  },
  {
    key: 'glow',
    label: 'Brilho colorido',
    version: '1.0.0',
    supportedRenderModes: WEB_E_PDF,
    fallback: 'ring',
    performanceCost: 'low',
    css: 'box-shadow: 0 0 0 1px rgba(255,255,255,.08), 0 30px 90px -20px rgba(59,130,246,.55);',
  },
  {
    key: 'gradient-border',
    label: 'Borda em degradê',
    description: 'Contorno colorido. Onde não há suporte vira contorno fino.',
    version: '1.0.0',
    supportedRenderModes: WEB,
    fallback: 'ring',
    performanceCost: 'medium',
    css:
      'position: relative; border: 1px solid transparent;' +
      ' background-image: linear-gradient(var(--st-fx-bg, #ffffff), var(--st-fx-bg, #ffffff)),' +
      ' linear-gradient(135deg, #6366f1, #22d3ee 55%, #f472b6);' +
      ' background-origin: border-box; background-clip: padding-box, border-box;',
  },
  {
    key: 'noise',
    label: 'Textura fina',
    description: 'Granulado sutil que tira o aspecto "chapado".',
    version: '1.0.0',
    supportedRenderModes: WEB,
    fallback: null,
    performanceCost: 'medium',
    css:
      "background-image: radial-gradient(rgba(148,163,184,.20) .6px, transparent .6px);" +
      ' background-size: 3px 3px;',
  },
  {
    key: 'spotlight',
    label: 'Foco de luz',
    version: '1.0.0',
    supportedRenderModes: WEB,
    fallback: null,
    performanceCost: 'medium',
    css:
      'background-image: radial-gradient(420px 240px at 50% 0%, rgba(255,255,255,.22), transparent 70%);',
  },
  {
    key: 'inner-shadow',
    label: 'Sombra interna',
    version: '1.0.0',
    supportedRenderModes: WEB_E_PDF,
    fallback: null,
    performanceCost: 'low',
    css: 'box-shadow: inset 0 1px 0 rgba(255,255,255,.18), inset 0 -24px 48px -32px rgba(2,6,23,.85);',
  },
  {
    key: 'fade-edges',
    label: 'Bordas esmaecidas',
    description: 'As pontas somem suavemente (bom para faixas de logos).',
    version: '1.0.0',
    supportedRenderModes: WEB,
    fallback: null,
    performanceCost: 'low',
    css:
      '-webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);' +
      ' mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);',
  },
];

const MAPA = new Map<string, StudioEffectDefinition>(EFEITOS.map((e) => [e.key, e]));

/** Retorna a definição de um efeito, ou `undefined`. */
export function getEffect(key: string): StudioEffectDefinition | undefined {
  return MAPA.get(key);
}

/** Lista todos os efeitos registrados. */
export function listEffects(): StudioEffectDefinition[] {
  return [...EFEITOS];
}

/** Opções prontas para o painel de propriedades. */
export const STUDIO_EFFECT_OPTIONS = EFEITOS.map((e) => ({ value: e.key, label: e.label }));

/**
 * Resolve o efeito para o modo de saída pedido, seguindo a corrente de
 * substitutos. Devolve `null` quando nada é aplicável (o elemento sai limpo).
 */
export function resolveEffect(
  key: string | undefined | null,
  mode: StudioEffectRenderMode = 'published',
): StudioEffectDefinition | null {
  // Miniatura NUNCA recebe efeito (Fase 7): a galeria precisa ser leve e igual.
  if (mode === 'thumbnail') return null;
  let atual = key ? MAPA.get(String(key)) : undefined;
  // Limite de saltos evita laço caso alguém registre um substituto circular.
  for (let salto = 0; salto < 5; salto += 1) {
    if (!atual || atual.key === 'none') return null;
    if (atual.supportedRenderModes.includes(mode)) return atual;
    atual = atual.fallback ? MAPA.get(atual.fallback) : undefined;
  }
  return null;
}

/** Lê a chave do efeito gravada no bloco (`props.effect`). */
export function lerEfeito(props: unknown): string | null {
  if (!props || typeof props !== 'object') return null;
  const valor = (props as Record<string, unknown>).effect;
  return typeof valor === 'string' && valor && valor !== 'none' ? valor : null;
}

/**
 * Classe CSS do efeito já resolvida para o modo de saída.
 * String vazia quando o bloco não pediu efeito ou o meio não suporta nenhum.
 */
export function classeDoEfeito(
  props: unknown,
  mode: StudioEffectRenderMode = 'published',
): string {
  const efeito = resolveEffect(lerEfeito(props), mode);
  return efeito ? `st-fx-${efeito.key}` : '';
}

/**
 * CSS de todos os efeitos, gerado a partir do próprio registro.
 * É incluído na fonte única de estilo, portanto vale para o editor, para a
 * página publicada e para o PDF sem nenhuma cópia manual.
 */
export function effectStyles(): string {
  return EFEITOS.filter((e) => e.css)
    .map((e) => `      .st-fx-${e.key} { ${e.css} }`)
    .join('\n');
}


// =============================================================================
// REGISTRO VISUAL — recortes (máscaras), inclinação e formas (Fase 6)
//
// Mora neste mesmo arquivo (e não em um módulo à parte) para continuar sendo
// empacotado pelas Edge Functions junto com o registro de efeitos. Vale a mesma
// regra: contrato obrigatório (versão, meios suportados, substituto e custo) e
// CSS emitido pelo próprio catálogo.
// =============================================================================


/** Modos de saída atendidos pelo Studio. */
export type StudioVisualRenderMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Custo aproximado de renderização. */
export type StudioVisualCost = 'low' | 'medium' | 'high';

/** Contrato de um recorte (máscara). */
export interface StudioMaskDefinition {
  key: string;
  label: string;
  description?: string;
  version: string;
  supportedRenderModes: StudioVisualRenderMode[];
  /** Recorte usado quando o meio não suporta este. `null` = sem recorte. */
  fallback: string | null;
  performanceCost: StudioVisualCost;
  /** CSS do recorte (classe `.st-mk-<chave>`). */
  css: string;
}

/** Contrato de uma forma decorativa. */
export interface StudioShapeDefinition {
  key: string;
  label: string;
  version: string;
  supportedRenderModes: StudioVisualRenderMode[];
  performanceCost: StudioVisualCost;
  css: string;
}

const V_WEB: StudioVisualRenderMode[] = ['editor', 'published', 'thumbnail'];
const V_WEB_E_PDF: StudioVisualRenderMode[] = [...V_WEB, 'pdf'];
const V_TODOS: StudioVisualRenderMode[] = [...V_WEB_E_PDF, 'email'];

// =============================================================================
// Recortes (máscaras)
// =============================================================================

const MASCARAS: StudioMaskDefinition[] = [
  {
    key: 'none',
    label: 'Nenhum',
    version: '1.0.0',
    supportedRenderModes: V_TODOS,
    fallback: null,
    performanceCost: 'low',
    css: '',
  },
  {
    key: 'circle',
    label: 'Círculo',
    description: 'Recorta a mídia em um círculo perfeito.',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    fallback: null,
    performanceCost: 'low',
    css: 'clip-path: circle(50% at 50% 50%);',
  },
  {
    key: 'rounded',
    label: 'Cantos suaves',
    description: 'Arredondamento generoso, estilo cartão.',
    version: '1.0.0',
    supportedRenderModes: V_TODOS,
    fallback: null,
    performanceCost: 'low',
    css: 'border-radius: 28px; overflow: hidden;',
  },
  {
    key: 'organic',
    label: 'Orgânico',
    description: 'Contorno irregular, com ar artesanal.',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    fallback: 'rounded',
    performanceCost: 'low',
    css: 'border-radius: 58% 42% 46% 54% / 46% 52% 48% 54%; overflow: hidden;',
  },
  {
    key: 'arch',
    label: 'Arco',
    description: 'Topo em meia-lua, base reta.',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    fallback: 'rounded',
    performanceCost: 'low',
    css: 'border-radius: 999px 999px 20px 20px; overflow: hidden;',
  },
  {
    key: 'diagonal',
    label: 'Diagonal',
    description: 'Corte inclinado na base.',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    fallback: null,
    performanceCost: 'low',
    css: 'clip-path: polygon(0 0, 100% 0, 100% 86%, 0 100%);',
  },
  {
    key: 'wave',
    label: 'Onda',
    description: 'Base curvada, como uma onda suave.',
    version: '1.0.0',
    supportedRenderModes: V_WEB,
    fallback: 'rounded',
    performanceCost: 'medium',
    css: 'clip-path: ellipse(150% 92% at 50% 8%);',
  },
  {
    key: 'custom',
    label: 'Desenho próprio',
    description: 'Usa o traçado informado nas propriedades avançadas.',
    version: '1.0.0',
    supportedRenderModes: V_WEB,
    fallback: 'rounded',
    performanceCost: 'medium',
    // O traçado entra por variável, já limpo por `sanitizeSvgPath`.
    css: 'clip-path: var(--st-mask, none);',
  },
];

const MAPA_MASCARAS = new Map<string, StudioMaskDefinition>(MASCARAS.map((m) => [m.key, m]));

/** Opções prontas para o painel de propriedades. */
export const STUDIO_MASK_OPTIONS = MASCARAS.map((m) => ({ value: m.key, label: m.label }));

/** Retorna a definição de um recorte. */
export function getMask(key: string): StudioMaskDefinition | undefined {
  return MAPA_MASCARAS.get(key);
}

/** Lista todos os recortes registrados. */
export function listMasks(): StudioMaskDefinition[] {
  return [...MASCARAS];
}

/** Resolve o recorte para o modo pedido, seguindo a corrente de substitutos. */
export function resolveMask(
  key: string | undefined | null,
  mode: StudioVisualRenderMode = 'published',
): StudioMaskDefinition | null {
  let atual = key ? MAPA_MASCARAS.get(String(key)) : undefined;
  for (let salto = 0; salto < 5; salto += 1) {
    if (!atual || atual.key === 'none') return null;
    if (atual.supportedRenderModes.includes(mode)) return atual;
    atual = atual.fallback ? MAPA_MASCARAS.get(atual.fallback) : undefined;
  }
  return null;
}

// =============================================================================
// Limpeza estrita do traçado próprio (SVG)
// =============================================================================

/** Tamanho máximo aceito para o traçado (protege o desempenho e o payload). */
export const STUDIO_SVG_PATH_MAX = 2000;

/**
 * Limpa o traçado de um `path` SVG.
 *
 * Aceita SOMENTE letras de comando (M m L l H h V v C c S s Q q T t A a Z z),
 * números, ponto, sinal, vírgula, espaço e `e/E` de notação científica. Qualquer
 * outra coisa — `<`, `script`, `url(`, `javascript:`, entidades, `foreignObject`
 * — invalida o traçado inteiro (devolve string vazia). É proposital: melhor sair
 * sem recorte do que aceitar conteúdo duvidoso.
 */
export function sanitizeSvgPath(valor: unknown): string {
  if (typeof valor !== 'string') return '';
  const bruto = valor.trim();
  if (!bruto || bruto.length > STUDIO_SVG_PATH_MAX) return '';
  if (!/^[MmLlHhVvCcSsQqTtAaZz0-9.,\-+eE\s]+$/.test(bruto)) return '';
  // Precisa começar por um comando de movimento — evita "número solto".
  if (!/^[Mm]/.test(bruto)) return '';
  return bruto.replace(/\s+/g, ' ');
}

// =============================================================================
// Inclinação (perspectiva leve)
// =============================================================================

/** Opções de inclinação. */
export const STUDIO_TILT_OPTIONS = [
  { value: 'none', label: 'Sem inclinação' },
  { value: 'left', label: 'Levemente à esquerda' },
  { value: 'right', label: 'Levemente à direita' },
  { value: 'up', label: 'Levemente para cima' },
];

const TILTS = new Set(['left', 'right', 'up']);

// =============================================================================
// Formas decorativas
// =============================================================================

const FORMAS: StudioShapeDefinition[] = [
  {
    key: 'orb',
    label: 'Orbe',
    version: '1.0.0',
    supportedRenderModes: V_WEB,
    performanceCost: 'medium',
    css:
      'border-radius: 999px;' +
      ' background: radial-gradient(circle at 32% 30%, var(--st-shape, rgba(99,102,241,.55)), transparent 70%);' +
      ' filter: blur(6px);',
  },
  {
    key: 'ring',
    label: 'Anel',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    performanceCost: 'low',
    css: 'border-radius: 999px; border: 1px solid var(--st-shape, rgba(148,163,184,.45));',
  },
  {
    key: 'beam',
    label: 'Feixe de luz',
    version: '1.0.0',
    supportedRenderModes: V_WEB,
    performanceCost: 'medium',
    css:
      'background: linear-gradient(120deg, transparent, var(--st-shape, rgba(255,255,255,.28)), transparent);' +
      ' transform: rotate(-18deg); filter: blur(10px);',
  },
  {
    key: 'line',
    label: 'Linha',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    performanceCost: 'low',
    css:
      'background: linear-gradient(90deg, transparent, var(--st-shape, rgba(148,163,184,.6)), transparent);' +
      ' height: 1px;',
  },
  {
    key: 'wave',
    label: 'Onda',
    version: '1.0.0',
    supportedRenderModes: V_WEB,
    performanceCost: 'medium',
    css:
      'background: radial-gradient(closest-side, transparent 96%, var(--st-shape, rgba(148,163,184,.4)) 100%);' +
      ' background-size: 56px 28px; background-repeat: repeat-x;',
  },
  {
    key: 'grid',
    label: 'Grade',
    version: '1.0.0',
    supportedRenderModes: V_WEB_E_PDF,
    performanceCost: 'low',
    css:
      'background-image: linear-gradient(var(--st-shape, rgba(148,163,184,.22)) 1px, transparent 1px),' +
      ' linear-gradient(90deg, var(--st-shape, rgba(148,163,184,.22)) 1px, transparent 1px);' +
      ' background-size: 40px 40px;',
  },
];

const MAPA_FORMAS = new Map<string, StudioShapeDefinition>(FORMAS.map((f) => [f.key, f]));

/** Opções prontas para o painel de propriedades. */
export const STUDIO_SHAPE_OPTIONS = FORMAS.map((f) => ({ value: f.key, label: f.label }));

/** Tamanhos oferecidos à forma decorativa. */
export const STUDIO_SHAPE_SIZE_OPTIONS = [
  { value: 'sm', label: 'Pequena' },
  { value: 'md', label: 'Média' },
  { value: 'lg', label: 'Grande' },
  { value: 'xl', label: 'Enorme' },
];

/** Lista as formas registradas. */
export function listShapes(): StudioShapeDefinition[] {
  return [...FORMAS];
}

/**
 * Classe da forma decorativa resolvida para o modo de saída.
 * Formas são só enfeite: quando o meio não suporta, não são desenhadas.
 */
export function classeDaForma(
  key: string | undefined | null,
  size: string | undefined | null,
  mode: StudioVisualRenderMode = 'published',
): string {
  const forma = key ? MAPA_FORMAS.get(String(key)) : undefined;
  if (!forma || !forma.supportedRenderModes.includes(mode)) return '';
  const tamanho = ['sm', 'md', 'lg', 'xl'].includes(String(size)) ? String(size) : 'md';
  return `st-shape st-shape-${forma.key} st-shape-${tamanho}`;
}

// =============================================================================
// Leitura das propriedades do bloco
// =============================================================================

function lerTexto(props: unknown, chave: string): string {
  if (!props || typeof props !== 'object') return '';
  const valor = (props as Record<string, unknown>)[chave];
  return typeof valor === 'string' ? valor : '';
}

/** `true` quando o bloco pediu algum acabamento visual (recorte ou inclinação). */
export function temVisual(props: unknown): boolean {
  const mascara = lerTexto(props, 'mask');
  const inclinacao = lerTexto(props, 'tilt');
  return (!!mascara && mascara !== 'none') || TILTS.has(inclinacao);
}

/**
 * Classes de acabamento visual do bloco, já resolvidas para o modo de saída.
 * String vazia quando não há nada aplicável.
 */
export function classesDoVisual(
  props: unknown,
  mode: StudioVisualRenderMode = 'published',
): string {
  const partes: string[] = [];
  const mascara = resolveMask(lerTexto(props, 'mask'), mode);
  if (mascara) partes.push(`st-mk-${mascara.key}`);
  const inclinacao = lerTexto(props, 'tilt');
  // Inclinação é perspectiva: não faz sentido em impressão nem em e-mail.
  if (TILTS.has(inclinacao) && (mode === 'editor' || mode === 'published')) {
    partes.push(`st-tilt-${inclinacao}`);
  }
  return partes.join(' ');
}

/**
 * Estilo em linha do acabamento visual (hoje só o traçado próprio).
 * Devolve string vazia quando não há nada a declarar.
 */
export function estiloDoVisual(
  props: unknown,
  mode: StudioVisualRenderMode = 'published',
): string {
  const mascara = resolveMask(lerTexto(props, 'mask'), mode);
  if (!mascara || mascara.key !== 'custom') return '';
  const traco = sanitizeSvgPath(lerTexto(props, 'maskPath'));
  return traco ? `--st-mask:path('${traco}');` : '';
}

/**
 * CSS de recortes, inclinações e formas, gerado a partir do próprio registro.
 * Entra na fonte única de estilo — vale para editor, página publicada e PDF.
 */
export function visualStyles(): string {
  const recortes = MASCARAS.filter((m) => m.css)
    .map((m) => `      .st-mk-${m.key} { ${m.css} }`)
    .join('\n');

  const formas = FORMAS.map((f) => `      .st-shape-${f.key} { ${f.css} }`).join('\n');

  return `
${recortes}

      .st-tilt-left { transform: perspective(1400px) rotateY(7deg) rotateX(2deg); }
      .st-tilt-right { transform: perspective(1400px) rotateY(-7deg) rotateX(2deg); }
      .st-tilt-up { transform: perspective(1400px) rotateX(8deg); }

      .st-shape { display: block; pointer-events: none; width: 100%; }
      .st-shape-sm { height: 120px; max-width: 120px; }
      .st-shape-md { height: 220px; max-width: 220px; }
      .st-shape-lg { height: 360px; max-width: 360px; }
      .st-shape-xl { height: 520px; max-width: 520px; }
      .st-shape-line, .st-shape-wave, .st-shape-grid { max-width: none; }
      .st-shape-line.st-shape-sm, .st-shape-line.st-shape-md,
      .st-shape-line.st-shape-lg, .st-shape-line.st-shape-xl { height: 1px; }
      .st-shape-wave.st-shape-sm { height: 28px; }
      .st-shape-wave.st-shape-md { height: 40px; }
      .st-shape-wave.st-shape-lg { height: 56px; }
      .st-shape-wave.st-shape-xl { height: 72px; }

      @media print {
        .st-tilt-left, .st-tilt-right, .st-tilt-up { transform: none; }
        .st-shape { display: none; }
      }`;
}


// =============================================================================
// DIREÇÃO DE ARTE POR TELA E ORÇAMENTOS (Fase 7)
//
// Mora neste mesmo arquivo pelo mesmo motivo do registro visual: continuar
// sendo empacotado pelas Edge Functions. Reúne três coisas:
//  1. VISIBILIDADE E ORDEM por tamanho de tela (direção de arte);
//  2. REDUÇÃO AUTOMÁTICA de intensidade no celular (desempenho);
//  3. ORÇAMENTO de desempenho e de interação, usado pela validação de
//     publicação para AVISAR e BLOQUEAR acúmulo de efeito caro.
// =============================================================================

import { STUDIO_MOTION_BUDGET, getMotion, lerMovimento } from './motion.ts';

/** Onde o bloco aparece. Uma escolha só, em linguagem de usuário. */
export const STUDIO_SCREEN_OPTIONS = [
  { value: 'all', label: 'Em todos os tamanhos' },
  { value: 'desktop', label: 'Só no computador' },
  { value: 'tablet', label: 'Só no tablet' },
  { value: 'mobile', label: 'Só no celular' },
  { value: 'not-mobile', label: 'Menos no celular' },
  { value: 'not-desktop', label: 'Só em tablet e celular' },
  { value: 'screen', label: 'Só na tela (fora da impressão e do e-mail)' },
  { value: 'print', label: 'Só na impressão e no e-mail' },
];

const VISIBILIDADES = new Set(STUDIO_SCREEN_OPTIONS.map((o) => o.value));

/** Ordem de exibição dentro da mesma faixa (útil para inverter no celular). */
export const STUDIO_SCREEN_ORDER_OPTIONS = [
  { value: '', label: 'Padrão' },
  { value: '1', label: 'Primeiro' },
  { value: '2', label: 'Segundo' },
  { value: '3', label: 'Terceiro' },
  { value: '4', label: 'Quarto' },
  { value: '5', label: 'Quinto' },
  { value: '6', label: 'Sexto' },
];

/** Lê a visibilidade escolhida no bloco (`props.showOn`). */
export function lerVisibilidade(props: unknown): string {
  if (!props || typeof props !== 'object') return 'all';
  const valor = (props as Record<string, unknown>).showOn;
  const chave = typeof valor === 'string' ? valor : 'all';
  return VISIBILIDADES.has(chave) ? chave : 'all';
}

/**
 * Diz se o bloco deve sequer ser gerado no meio pedido.
 * Impressão e e-mail não têm "tamanho de tela": valem as regras de computador.
 */
export function blocoVisivelNoModo(
  props: unknown,
  mode: StudioVisualRenderMode = 'published',
): boolean {
  const chave = lerVisibilidade(props);
  if (chave === 'all') return true;
  const impresso = mode === 'pdf' || mode === 'email';
  if (impresso) return chave === 'print' || chave === 'desktop' || chave === 'not-mobile';
  return chave !== 'print';
}

/** Classe de visibilidade por tela (só faz sentido nos meios de tela). */
export function classeDeVisibilidade(
  props: unknown,
  mode: StudioVisualRenderMode = 'published',
): string {
  if (mode === 'pdf' || mode === 'email') return '';
  const chave = lerVisibilidade(props);
  return chave === 'all' ? '' : `st-vis-${chave}`;
}

/** Lê a ordem por tela (`props.order`, aceitando valor único ou por tela). */
export function estiloDaOrdem(props: unknown): string {
  if (!props || typeof props !== 'object') return '';
  const bruto = (props as Record<string, unknown>).order;
  if (bruto === undefined || bruto === null || bruto === '') return '';
  const mapa =
    typeof bruto === 'object' && !Array.isArray(bruto)
      ? (bruto as Record<string, unknown>)
      : { desktop: bruto };
  const partes: string[] = [];
  (['desktop', 'tablet', 'mobile'] as const).forEach((tela) => {
    const valor = mapa[tela];
    if (valor === undefined || valor === null || valor === '') return;
    const numero = Number(valor);
    if (!Number.isFinite(numero) || numero < 1 || numero > 12) return;
    const sufixo = tela === 'desktop' ? 'd' : tela === 'tablet' ? 't' : 'm';
    partes.push(`--st-ord-${sufixo}:${Math.trunc(numero)}`);
  });
  return partes.length ? `${partes.join(';')}` : '';
}

/** Verdadeiro quando o bloco pediu alguma regra de tela (visibilidade/ordem). */
export function temRegraDeTela(props: unknown): boolean {
  return lerVisibilidade(props) !== 'all' || Boolean(estiloDaOrdem(props));
}

// -----------------------------------------------------------------------------
// Orçamento de desempenho
// -----------------------------------------------------------------------------

/**
 * Teto de custo de uma página.
 * `pontos` é a soma do custo de efeitos, recortes e movimentos (baixo = 1,
 * médio = 3, alto = 8). `pesados` conta apenas os recursos de custo alto.
 */
export const STUDIO_PERFORMANCE_BUDGET = {
  /** A partir daqui o Studio AVISA. */
  avisoPontos: 60,
  /** A partir daqui o Studio BLOQUEIA a publicação. */
  bloqueioPontos: 100,
  /** Teto de recursos de custo alto na mesma página. */
  pesadosMax: 8,
} as const;

const PESO: Record<string, number> = { low: 1, medium: 3, high: 8 };

/** Custo de um bloco isolado (efeito + recorte + movimento). */
export function custoDoBloco(props: unknown): { pontos: number; pesados: number } {
  let pontos = 0;
  let pesados = 0;
  const somar = (custo?: string) => {
    if (!custo) return;
    pontos += PESO[custo] ?? 1;
    // "Pesado" é tudo que exige o navegador trabalhar a cada quadro: vidro,
    // ruído, brilho de fundo, recorte complexo e animação em laço.
    if (custo === 'high') pesados += 2;
    else if (custo === 'medium') pesados += 1;
  };

  const efeito = getEffect(String(lerEfeito(props) ?? ''));
  if (efeito) somar(efeito.performanceCost);

  const recorte = props && typeof props === 'object'
    ? getMask(String((props as Record<string, unknown>).mask ?? ''))
    : undefined;
  if (recorte && recorte.key !== 'none') somar(recorte.performanceCost);

  const movimento = getMotion(String(lerMovimento(props) ?? ''));
  if (movimento) somar(movimento.performanceCost);

  return { pontos, pesados };
}

/** Resultado da conta do orçamento de uma página. */
export interface StudioBudgetReport {
  pontos: number;
  pesados: number;
  animacoes: number;
  laços: number;
  /** Passou do teto de aviso (mostra recado, mas publica). */
  aviso: boolean;
  /** Passou do teto de bloqueio (não publica). */
  bloqueia: boolean;
  /** Frases prontas, em linguagem de usuário. */
  mensagens: string[];
}

/** Soma o custo de uma página inteira, a partir das propriedades dos blocos. */
export function avaliarOrcamento(blocos: unknown[]): StudioBudgetReport {
  let pontos = 0;
  let pesados = 0;
  let animacoes = 0;
  let laços = 0;

  (blocos ?? []).forEach((props) => {
    const custo = custoDoBloco(props);
    pontos += custo.pontos;
    pesados += custo.pesados;
    const movimento = getMotion(String(lerMovimento(props) ?? ''));
    if (movimento && movimento.key !== 'none') {
      animacoes += 1;
      if (movimento.loop) laços += 1;
    }
  });

  const mensagens: string[] = [];
  const bloqueia =
    pontos > STUDIO_PERFORMANCE_BUDGET.bloqueioPontos ||
    pesados > STUDIO_PERFORMANCE_BUDGET.pesadosMax;
  const aviso = !bloqueia && pontos > STUDIO_PERFORMANCE_BUDGET.avisoPontos;

  if (pesados > STUDIO_PERFORMANCE_BUDGET.pesadosMax) {
    mensagens.push(
      `A página tem ${pesados} recursos pesados (vidro, ruído, brilho, recorte complexo, animação em laço). O limite é ${STUDIO_PERFORMANCE_BUDGET.pesadosMax}; retire alguns para publicar.`,
    );
  }
  if (pontos > STUDIO_PERFORMANCE_BUDGET.bloqueioPontos) {
    mensagens.push(
      'A soma de efeitos, recortes e animações passou do limite de desempenho. A página ficaria lenta no celular; simplifique alguns blocos.',
    );
  } else if (aviso) {
    mensagens.push(
      'A página está carregada de efeitos. Ainda dá para publicar, mas o celular vai receber uma versão mais leve automaticamente.',
    );
  }
  if (animacoes > STUDIO_MOTION_BUDGET.total) {
    mensagens.push(
      `Há ${animacoes} animações; só as ${STUDIO_MOTION_BUDGET.total} primeiras vão acontecer.`,
    );
  }
  if (laços > STUDIO_MOTION_BUDGET.loops) {
    mensagens.push(
      `Há ${laços} animações em laço; só as ${STUDIO_MOTION_BUDGET.loops} primeiras vão acontecer.`,
    );
  }

  return { pontos, pesados, animacoes, laços, aviso, bloqueia, mensagens };
}

/**
 * CSS da direção de arte por tela e da redução automática no celular.
 * Emitido pelos próprios registros: efeito de custo alto perde o peso no
 * celular, laço para de girar e a miniatura nunca anima.
 */
export function screenStyles(): string {
  const pesados = EFEITOS.filter((e) => e.performanceCost !== 'low')
    .map((e) => `.st-fx-${e.key}`)
    .join(', ');

  return `
      .st-vis-desktop, .st-vis-tablet, .st-vis-mobile,
      .st-vis-not-mobile, .st-vis-not-desktop, .st-vis-print { display: none; }
      .st-vis-screen { display: block; }
      @media (min-width: 1025px) {
        .st-vis-desktop, .st-vis-not-mobile { display: block; }
      }
      @media (min-width: 641px) and (max-width: 1024px) {
        .st-vis-tablet, .st-vis-not-mobile, .st-vis-not-desktop { display: block; }
      }
      @media (max-width: 640px) {
        .st-vis-mobile, .st-vis-not-desktop { display: block; }
      }
      [style*="--st-ord-d"] { order: var(--st-ord-d, 0); }
      @media (max-width: 1024px) {
        [style*="--st-ord-t"] { order: var(--st-ord-t, var(--st-ord-d, 0)); }
      }
      @media (max-width: 640px) {
        [style*="--st-ord-m"] { order: var(--st-ord-m, var(--st-ord-t, var(--st-ord-d, 0))); }
        ${pesados ? `${pesados} { backdrop-filter: none; -webkit-backdrop-filter: none; filter: none; box-shadow: 0 8px 20px rgba(15,23,42,.10); }` : ''}
        .st-shape { opacity: .6; }
      }
      @media print {
        .st-vis-screen { display: none; }
        .st-vis-print { display: block; }
      }`;
}
